import { ReactNode } from "react";
import { Route, Switch, useLocation } from "wouter";
import {
  RiDashboardLine,
  RiDownloadLine,
  RiKey2Line,
  RiKeyLine,
  RiLinkM,
  RiLoopLeftLine,
  RiShieldCheckLine,
} from "@remixicon/react";
import { useUser } from "../context/UserContext";
import AuthorizationCodeRedirect from "./AuthorizationCodePage";
import { isReadOnly } from "../config";

interface AuthenticatedLayoutProps {
  children: ReactNode;
}

const navItems = [
  { label: "Overzicht", route: "/", icon: RiDashboardLine, module: "sync_hub" },
  { label: "Authenticatie", route: "/authenticatie", icon: RiShieldCheckLine, module: "sync_hub" },
  { label: "Administraties", route: "/administraties", icon: RiKey2Line },
  { label: "Koppelingen", route: "/koppelingen", icon: RiLinkM, module: "sync_hub" },
  { label: "Synchronisaties", route: "/synchronisaties", icon: RiLoopLeftLine, module: "sync_hub" },
  { label: "DataStudio", route: "/datastudio", icon: RiDownloadLine, module: "data_studio" },
  { label: "API-sleutels", route: "/api-sleutels", icon: RiKeyLine, module: "data_studio" },
] as const;

export const AuthenticatedLayout = ({ children }: AuthenticatedLayoutProps) => {
  const { user, sessionToken } = useUser();
  const [location, setLocation] = useLocation();

  if (!user || !sessionToken) {
    return (
      <Switch>
        <Route path="/authorization-code" component={() => <AuthorizationCodeRedirect />} />
        <Route>
          {() => (
            <div className="flex min-h-screen w-full items-center justify-center bg-page">
              <div className="w-full max-w-md rounded-xl bg-[#3D557E] p-8 shadow-lg">
                <div className="flex flex-col items-center gap-4 text-center">
                  <h2 className="text-lg font-semibold text-white">Niet ingelogd</h2>
                  <p className="text-sm text-white/70">
                    Open het portaal vanuit Easyflex om in te loggen.
                  </p>
                </div>
              </div>
            </div>
          )}
        </Route>
      </Switch>
    );
  }

  const isActive = (route: string) => {
    if (route === "/") return location === "/";
    return location.startsWith(route);
  };

  const visibleNavItems = navItems
    .filter((item) => !item.module || item.module === user.module);

  return (
    <div className="flex h-screen flex-col bg-page">
      {isReadOnly && (
        <div className="bg-amber-100 px-6 py-2 text-center text-sm font-medium text-amber-900">
          Support Omgeving SyncHub
        </div>
      )}
      <nav className="flex border-b border-gray-200 bg-white px-6">
        {visibleNavItems.map((item) => {
          const active = isActive(item.route);
          return (
            <button
              key={item.route}
              onClick={() => setLocation(item.route)}
              className={`group flex items-center gap-2.5 border-b-2 px-5 pt-5 pb-3 text-base font-medium transition-colors duration-150
                ${active
                  ? "border-[#227D80] text-[#227D80]"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
            >
              <span
                className={`flex items-center justify-center rounded-md p-1.5 transition-colors duration-150
                  ${active ? "bg-[#227D80]" : "bg-gray-300 group-hover:bg-gray-400"}`}
              >
                <item.icon size={18} className="shrink-0 text-white" />
              </span>
              {item.label}
            </button>
          );
        })}
      </nav>

      <main className="flex-1 overflow-y-auto p-8">
        {children}
      </main>
    </div>
  );
};
